/**
 * The words of **Who can sign in** — a business's staff logins on the Setup
 * tab (`ui/business/setup/Logins.tsx`, `docs/runbooks/staff-logins.md`).
 *
 * One file per area, like the others: `en.ts` and `es.ts` spread these in, and
 * the Spanish half is typed against the English one, so a key missing here is
 * a build failure.
 *
 * The plan's principle 2 for this card: a role is named by what the person can
 * do ("Runs the day"), never by its token; a login's state is a short phrase
 * ("hasn’t signed in yet"); what the record and Google disagree about is a
 * sentence with the fix beside it. And the one promise the card makes out loud,
 * because it is the reason the flow exists: **nobody here ever sees their
 * password** — Google emails the link to them.
 */
export const loginsEn = {
  'logins.title': 'Who can sign in',
  'logins.lede': ({ name }: { name: string }) =>
    `The people who sign in to ${name}’s staff app. Each one chooses their own password from an email Google sends them — nobody here ever sees it.`,
  'logins.none': ({ name }: { name: string }) => `Nobody can sign in to ${name}’s staff app yet.`,
  'logins.count': ({ count }: { count: number }) =>
    `${String(count)} ${count === 1 ? 'person' : 'people'}`,
  'logins.invite': 'Invite someone',

  /* ---- roles, in words -------------------------------------------- */
  'logins.role.owner': 'Owner',
  'logins.role.staff': 'Staff',
  'logins.role.readonly': 'Read-only',
  'logins.role.none': 'No role',
  'logins.role.owner.what':
    'Runs the business: changes what it sells and who works there, and runs the day.',
  'logins.role.staff.what': 'Runs the day: the book, the clients and the counter.',
  'logins.role.readonly.what':
    'Can look at everything and change nothing — a front-desk screen or an accountant.',

  /* ---- a login's state -------------------------------------------- */
  'logins.signedIn': ({ when }: { when: string }) => `signed in ${when}`,
  'logins.neverSignedIn': 'hasn’t signed in yet',
  'logins.off': 'switched off',
  'logins.removedState': 'access removed',

  /* ---- what the record and Google disagree about ------------------- */
  'logins.flag.unrecorded': 'Can sign in, but isn’t in our records yet.',
  'logins.flag.no_login':
    'Our records say they can sign in, but Google has no login for them here.',
  'logins.flag.record_differs':
    'Our record of them is out of date. Changing their role brings it up to date.',
  'logins.flag.unknown_role':
    'Their role isn’t one the app knows, so they can’t sign in. Give them one.',
  'logins.flag.other': 'Something about this login needs a look.',
  'logins.record': 'Record it',
  'logins.noOwner': ({ name }: { name: string }) =>
    `Nobody here is an owner. Only an owner can change what ${name} sells and who works there.`,
  'logins.truncated':
    'Google’s list of logins was too long to read in full, so someone may be missing here.',

  /* ---- the row's buttons ------------------------------------------ */
  'logins.resend': 'Resend email',
  'logins.changeRole': 'Change role',
  'logins.remove': 'Remove access',
  'logins.showRemoved': ({ count }: { count: number }) =>
    `Show ${String(count)} with access removed`,
  'logins.hideRemoved': 'Hide removed',

  /* ---- invite ----------------------------------------------------- */
  'logins.dialog.title': ({ name }: { name: string }) => `Invite someone to ${name}’s staff app`,
  'logins.dialog.email': 'Email',
  'logins.dialog.name': 'Name',
  'logins.dialog.nameHint': 'Optional. Shown in the app and on this list.',
  'logins.dialog.role': 'What they can do',
  'logins.dialog.how':
    'Google emails them a link to choose their own password. The link works once and runs out; you can send another from this list.',
  'logins.dialog.send': 'Send the invitation',
  'logins.dialog.sending': 'Sending…',
  'logins.invited': ({ email }: { email: string }) =>
    `Invited ${email}. Google is emailing them a link to choose a password.`,
  'logins.mailFailed': ({ email }: { email: string }) =>
    `${email} has a login, but the invitation email didn’t go out.`,
  'logins.mailFailedAction': 'Send it again',
  'logins.resent': ({ email }: { email: string }) =>
    `Sent ${email} another email to choose a password.`,

  /* ---- change role ------------------------------------------------ */
  'logins.roleDialog.title': ({ who }: { who: string }) => `Change what ${who} can do`,
  'logins.roleDialog.when':
    'It reaches their phone or tablet within the hour, the next time the app refreshes their sign-in.',
  'logins.roleDialog.save': 'Change role',
  'logins.roleChanged': ({ who, role }: { who: string; role: string }) => `${who} is now ${role}.`,

  /* ---- remove access ---------------------------------------------- */
  'logins.removeTitle': ({ who }: { who: string }) => `Remove ${who}’s access?`,
  'logins.removeConsequence': ({ who, name }: { who: string; name: string }) =>
    `${who} is signed out of ${name}’s staff app now and can’t sign in again. Nothing they did is deleted. To give access back, invite them again.`,
  'logins.removed': ({ who }: { who: string }) => `${who} can no longer sign in.`,
  'logins.recorded': ({ who }: { who: string }) => `Recorded ${who}.`,
};

export const loginsEs: typeof loginsEn = {
  'logins.title': 'Quién puede entrar',
  'logins.lede': ({ name }) =>
    `Las personas que entran a la app del personal de ${name}. Cada una elige su propia contraseña desde un correo que le envía Google — nadie aquí la ve nunca.`,
  'logins.none': ({ name }) => `Todavía nadie puede entrar a la app del personal de ${name}.`,
  'logins.count': ({ count }) => `${String(count)} ${count === 1 ? 'persona' : 'personas'}`,
  'logins.invite': 'Invitar a alguien',

  'logins.role.owner': 'Dueño',
  'logins.role.staff': 'Personal',
  'logins.role.readonly': 'Solo lectura',
  'logins.role.none': 'Sin rol',
  'logins.role.owner.what':
    'Dirige el negocio: cambia lo que vende y quién trabaja ahí, y lleva el día.',
  'logins.role.staff.what': 'Lleva el día: la agenda, los clientes y el mostrador.',
  'logins.role.readonly.what':
    'Puede ver todo y no cambiar nada — una pantalla de recepción o un contador.',

  'logins.signedIn': ({ when }) => `entró ${when}`,
  'logins.neverSignedIn': 'todavía no ha entrado',
  'logins.off': 'desactivado',
  'logins.removedState': 'acceso quitado',

  'logins.flag.unrecorded': 'Puede entrar, pero todavía no está en nuestros registros.',
  'logins.flag.no_login':
    'Nuestros registros dicen que puede entrar, pero Google no tiene un acceso suyo aquí.',
  'logins.flag.record_differs':
    'Nuestro registro de esta persona está desactualizado. Cambiar su rol lo pone al día.',
  'logins.flag.unknown_role':
    'Su rol no es uno que la app conozca, así que no puede entrar. Asígnele uno.',
  'logins.flag.other': 'Algo de este acceso necesita revisión.',
  'logins.record': 'Registrarlo',
  'logins.noOwner': ({ name }) =>
    `Nadie aquí es dueño. Solo un dueño puede cambiar lo que vende ${name} y quién trabaja ahí.`,
  'logins.truncated':
    'La lista de accesos de Google era demasiado larga para leerla completa, así que puede faltar alguien aquí.',

  'logins.resend': 'Reenviar el correo',
  'logins.changeRole': 'Cambiar el rol',
  'logins.remove': 'Quitar el acceso',
  'logins.showRemoved': ({ count }) => `Mostrar ${String(count)} sin acceso`,
  'logins.hideRemoved': 'Ocultar sin acceso',

  'logins.dialog.title': ({ name }) => `Invitar a alguien a la app del personal de ${name}`,
  'logins.dialog.email': 'Correo',
  'logins.dialog.name': 'Nombre',
  'logins.dialog.nameHint': 'Opcional. Se muestra en la app y en esta lista.',
  'logins.dialog.role': 'Lo que puede hacer',
  'logins.dialog.how':
    'Google le envía un enlace para elegir su propia contraseña. El enlace sirve una vez y vence; puede enviar otro desde esta lista.',
  'logins.dialog.send': 'Enviar la invitación',
  'logins.dialog.sending': 'Enviando…',
  'logins.invited': ({ email }) =>
    `Se invitó a ${email}. Google le está enviando un enlace para elegir una contraseña.`,
  'logins.mailFailed': ({ email }) =>
    `${email} tiene acceso, pero el correo de invitación no salió.`,
  'logins.mailFailedAction': 'Enviarlo de nuevo',
  'logins.resent': ({ email }) => `Se le envió a ${email} otro correo para elegir una contraseña.`,

  'logins.roleDialog.title': ({ who }) => `Cambiar lo que ${who} puede hacer`,
  'logins.roleDialog.when':
    'Llega a su teléfono o tableta en menos de una hora, la próxima vez que la app renueve su sesión.',
  'logins.roleDialog.save': 'Cambiar el rol',
  'logins.roleChanged': ({ who, role }) => `${who} ahora es ${role}.`,

  'logins.removeTitle': ({ who }) => `¿Quitarle el acceso a ${who}?`,
  'logins.removeConsequence': ({ who, name }) =>
    `${who} sale ahora de la app del personal de ${name} y no puede volver a entrar. No se borra nada de lo que hizo. Para devolverle el acceso, invítelo de nuevo.`,
  'logins.removed': ({ who }) => `${who} ya no puede entrar.`,
  'logins.recorded': ({ who }) => `Se registró a ${who}.`,
};
